'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/Badge';
import type { BlogCardData } from '@/types';

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Image box is `h-44` (176px). Fixed-width carousel slides pass `fixedWidth` so the
 *  image emits a 1x/2x srcset instead of the full responsive ladder (~2 KB per card). */
const THUMB_HEIGHT = 176;

function ImageWithFallback({ src, fallbackSrc, alt, className, sizes, fixedWidth }: {
  src: string; fallbackSrc: string; alt: string; className?: string; sizes?: string; fixedWidth?: number;
}) {
  const [imgSrc, setImgSrc] = useState(src);
  if (fixedWidth) {
    return (
      <Image
        src={imgSrc} alt={alt} width={fixedWidth} height={THUMB_HEIGHT}
        className={`${className ?? ''} h-full w-full`}
        onError={() => setImgSrc(fallbackSrc)}
      />
    );
  }
  return (
    <Image
      src={imgSrc} alt={alt} fill
      className={className} sizes={sizes}
      onError={() => setImgSrc(fallbackSrc)}
    />
  );
}

export function formatDate(dateStr: string, locale: string): string {
  return new Date(dateStr).toLocaleDateString(
    locale === 'ar' ? 'ar-EG' : 'en-US',
    { day: 'numeric', month: 'short', year: 'numeric' }
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────

export interface BlogCardLabels {
  readMore: string;
  min: string;
}

export interface BlogCardProps {
  post: BlogCardData;
  locale: string;
  labels: BlogCardLabels;
  /** Card is rendered at a fixed pixel width (carousel slide) — enables a lean 1x/2x image srcset */
  fixedWidth?: number;
  className?: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function BlogCard({ post, locale, labels, fixedWidth, className = '' }: BlogCardProps) {
  // Prefer the requested language, fall back to the other one so a post with only
  // one translation never renders an empty card or a broken /blog/undefined link.
  const title    = (locale === 'ar' ? (post.title.ar    || post.title.en)    : (post.title.en    || post.title.ar))    || post._id;
  const excerpt  = (locale === 'ar' ? (post.excerpt.ar  || post.excerpt.en)  : (post.excerpt.en  || post.excerpt.ar))  || '';
  const category = (locale === 'ar' ? (post.category?.name?.ar || post.category?.name?.en) : (post.category?.name?.en || post.category?.name?.ar)) || '';
  const slug     = (locale === 'ar' ? (post.slug.ar     || post.slug.en)     : (post.slug.en     || post.slug.ar))     || post._id;
  const categorySlug = post.category?.slug ? (locale === 'ar' ? post.category.slug.ar || post.category.slug.en : post.category.slug.en) : '';
  const altText  = (locale === 'ar' ? post.featuredImage?.alt?.ar : post.featuredImage?.alt?.en) || title;

  return (
    <article
      className={`
        group relative flex flex-col rounded-xl border border-(--border) bg-(--surface) overflow-hidden
        hover:border-primary-500/30 hover:shadow-md
        transition-all duration-250
        ${className}
      `}
    >
      {/* Image */}
      <div className="relative h-44 bg-(--bg) overflow-hidden">
        {post.featuredImage?.url ? (
          <ImageWithFallback
            src={post.featuredImage.url}
            fallbackSrc="/blog-image.png"
            alt={altText}
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width:640px) 100vw,(max-width:1024px) 50vw,33vw"
            fixedWidth={fixedWidth}
          />
        ) : (
          <ImageWithFallback
            src="/blog-image.png"
            fallbackSrc="/blog-image.png"
            alt={title}
            className="object-cover"
            sizes="(max-width:640px) 100vw,(max-width:1024px) 50vw,33vw"
            fixedWidth={fixedWidth}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          {categorySlug ? (
            <Link href={`/${locale}/blog/category/${encodeURIComponent(categorySlug)}`} className="relative z-1">
              <Badge color="primary" variant="soft" size="sm">{category}</Badge>
            </Link>
          ) : (
            <Badge color="primary" variant="soft" size="sm">{category}</Badge>
          )}
          {post.readingTime && (
            <span className="text-xs text-(--muted)">{post.readingTime} {labels.min}</span>
          )}
        </div>

        <h3 className="font-semibold text-(--text) leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors duration-180">
          {title}
        </h3>

        <p className="text-sm text-(--muted) leading-relaxed line-clamp-2">{excerpt}</p>

        <div className="flex items-center justify-between pt-1">
          {post.publishedAt && (
            <time dateTime={post.publishedAt} className="text-xs text-(--muted)">
              {formatDate(post.publishedAt, locale)}
            </time>
          )}
          <Link
            href={`/${locale}/blog/${slug}`}
            className="text-xs ms-auto font-medium text-primary-500 group-hover:text-primary-400 transition-colors after:absolute after:inset-0"
          >
            {labels.readMore}
            {/* Descriptive text for SEO/SR — link-text audit uses text content. */}
            <span className="sr-only"> {title}</span>
            {' '}→
          </Link>
        </div>
      </div>
    </article>
  );
}
